<template>
  <section
    class="regras-dia"
    :aria-labelledby="`${uid}-titulo`"
    data-testid="regras-tipo-dia-sindicato"
  >
    <header class="regras-dia__header">
      <span class="regras-dia__icone">
        <v-icon color="#5e72e4">mdi-calendar-clock</v-icon>
      </span>
      <div>
        <span class="regras-dia__eyebrow">{{ $t("Regras_dia_eyebrow") }}</span>
        <h3 :id="`${uid}-titulo`" class="regras-dia__titulo">
          {{ $t("Regras_dia_titulo") }}
        </h3>
        <p class="regras-dia__descricao">{{ $t("Regras_dia_descricao") }}</p>
      </div>
    </header>

    <div
      class="regras-abas"
      role="tablist"
      :aria-label="$t('Regras_dia_titulo')"
    >
      <button
        v-for="aba in abas"
        :key="aba.valor"
        type="button"
        role="tab"
        class="regras-aba"
        :class="{ 'regras-aba--ativa': abaAtual === aba.valor }"
        :aria-selected="String(abaAtual === aba.valor)"
        @click="abaAtual = aba.valor"
      >
        <v-icon small>{{ aba.icone }}</v-icon>
        {{ $t(aba.titulo) }}
        <span v-if="aba.contador" class="regras-aba__contador">
          {{ aba.contador }}
        </span>
      </button>
    </div>

    <!-- Tolerâncias -->
    <div v-if="abaAtual === 'TOLERANCIAS'" role="tabpanel">
      <p class="regras-dia__ajuda">
        <v-icon small color="#667085">mdi-scale-balance</v-icon>
        {{ $t("Regras_tolerancia_ajuda") }}
      </p>
      <div class="regras-tabela">
        <div class="regras-tabela__cabecalho regras-tolerancia__linha">
          <span>{{ $t("Regras_tipo_dia") }}</span>
          <span>{{ $t("Regras_tolerancia_extras") }}</span>
          <span>{{ $t("Regras_tolerancia_debitos") }}</span>
          <span>{{ $t("Regras_tolerancia_marcacao") }}</span>
        </div>
        <div
          v-for="linha in linhasTolerancia"
          :key="linha.situacao"
          class="regras-tolerancia__linha"
          :class="{ 'regras-tolerancia__linha--herdada': !linha.configuracao }"
        >
          <span class="regras-tolerancia__situacao">
            <v-switch
              :input-value="!!linha.configuracao"
              inset
              dense
              hide-details
              color="#5e72e4"
              class="mt-0 pt-0"
              :aria-label="`${$t('Regras_regra_propria')}: ${$t(
                `Situacao_dia_${linha.situacao}`
              )}`"
              @change="alternarTolerancia(linha.situacao, $event === true)"
            ></v-switch>
            <span>
              <strong>{{ $t(`Situacao_dia_${linha.situacao}`) }}</strong>
              <small v-if="!linha.configuracao">
                {{ $t("Regras_herda") }} {{ linha.heranca }}
              </small>
            </span>
          </span>
          <template v-if="linha.configuracao">
            <v-text-field
              :value="linha.configuracao.toleranciaExtrasMinutos"
              type="number"
              min="0"
              max="10"
              suffix="min"
              dense
              outlined
              hide-details
              class="regras-minutos"
              :aria-label="$t('Regras_tolerancia_extras')"
              @change="
                definirMinutos(
                  linha.configuracao,
                  'toleranciaExtrasMinutos',
                  $event
                )
              "
            ></v-text-field>
            <v-text-field
              :value="linha.configuracao.toleranciaDebitosMinutos"
              type="number"
              min="0"
              max="10"
              suffix="min"
              dense
              outlined
              hide-details
              class="regras-minutos"
              :aria-label="$t('Regras_tolerancia_debitos')"
              @change="
                definirMinutos(
                  linha.configuracao,
                  'toleranciaDebitosMinutos',
                  $event
                )
              "
            ></v-text-field>
            <v-switch
              :input-value="linha.configuracao.toleranciaPorMarcacao === true"
              inset
              dense
              hide-details
              color="#5e72e4"
              class="mt-0 pt-0"
              :aria-label="$t('Regras_tolerancia_marcacao')"
              @change="
                $set(
                  linha.configuracao,
                  'toleranciaPorMarcacao',
                  $event === true
                )
              "
            ></v-switch>
          </template>
          <template v-else>
            <span class="regras-tolerancia__herdado"
              >{{ linha.efetiva.extras }} min</span
            >
            <span class="regras-tolerancia__herdado"
              >{{ linha.efetiva.debitos }} min</span
            >
            <span class="regras-tolerancia__herdado">
              {{ linha.efetiva.porMarcacao ? $t("Sim") : $t("Nao") }}
            </span>
          </template>
        </div>
      </div>
      <p class="regras-dia__nota">
        {{ $t("Regras_tolerancia_marcacao_ajuda") }}
      </p>
    </div>

    <!-- Faixas -->
    <div v-if="abaAtual === 'FAIXAS'" role="tabpanel">
      <p class="regras-dia__ajuda">
        <v-icon small color="#667085">mdi-stairs</v-icon>
        {{ $t("Regras_faixas_ajuda") }}
      </p>

      <div v-if="!gruposFaixas.length" class="regras-vazio">
        {{ $t("Regras_faixas_vazio") }}
      </div>

      <div
        v-for="grupo in gruposFaixas"
        :key="grupo.chave"
        class="regras-grupo"
      >
        <div class="regras-grupo__cabecalho">
          <span>
            <strong>{{ $t(grupo.origem) }}</strong>
            <v-icon x-small>mdi-chevron-right</v-icon>
            {{ $t(`Situacao_dia_${grupo.situacao}`) }}
          </span>
          <v-btn
            icon
            x-small
            :aria-label="`${$t('Remover')}: ${$t(grupo.origem)} ${$t(
              `Situacao_dia_${grupo.situacao}`
            )}`"
            :title="$t('Remover')"
            @click="removerGrupo(grupo)"
          >
            <v-icon small>mdi-delete-outline</v-icon>
          </v-btn>
        </div>
        <ol class="regras-faixas">
          <li v-for="(nivel, indice) in grupo.niveis" :key="indice">
            <span class="regras-faixas__ordem">{{ indice + 1 }}</span>
            <span class="regras-faixas__limite">
              <template v-if="nivel.limiteHoras === null">
                {{ $t("Regras_faixa_restante") }}
              </template>
              <template v-else>
                {{ $t("Regras_faixa_ate") }}
                <v-text-field
                  :value="formatarHoras(nivel.limiteHoras * 60)"
                  placeholder="00:00"
                  dense
                  outlined
                  hide-details
                  class="regras-minutos"
                  :aria-label="$t('Regras_faixa_limite')"
                  @change="definirLimite(nivel, $event)"
                ></v-text-field>
                {{ $t("Regras_faixa_no_dia") }}
              </template>
            </span>
            <v-icon small color="#98a2b3">mdi-arrow-right</v-icon>
            <v-select
              :value="nivel.destino"
              :items="itensTipos"
              dense
              outlined
              hide-details
              class="regras-destino"
              :aria-label="$t('Regras_faixa_destino')"
              @change="$set(nivel, 'destino', $event)"
            ></v-select>
            <v-btn
              icon
              x-small
              :aria-label="$t('Remover')"
              :title="$t('Remover')"
              @click="removerNivel(nivel)"
            >
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </li>
        </ol>
        <div class="regras-grupo__acoes">
          <v-btn
            x-small
            text
            color="#5e72e4"
            class="text-none"
            :disabled="grupo.possuiRestante"
            @click="adicionarFaixa(grupo, false)"
          >
            <v-icon left x-small>mdi-plus</v-icon>
            {{ $t("Regras_adicionar_faixa_limite") }}
          </v-btn>
          <v-btn
            x-small
            text
            color="#5e72e4"
            class="text-none"
            :disabled="grupo.possuiRestante"
            @click="adicionarFaixa(grupo, true)"
          >
            <v-icon left x-small>mdi-plus</v-icon>
            {{ $t("Regras_adicionar_faixa_restante") }}
          </v-btn>
        </div>
      </div>

      <div class="regras-nova">
        <span>{{ $t("Regras_nova_regra") }}</span>
        <v-select
          v-model="novaRegra.origem"
          :items="itensTipos"
          :label="$t('Regras_tipo_apurado')"
          dense
          outlined
          hide-details
        ></v-select>
        <v-select
          v-model="novaRegra.situacao"
          :items="itensSituacoes"
          :label="$t('Regras_tipo_dia')"
          dense
          outlined
          hide-details
        ></v-select>
        <v-btn
          small
          color="#5e72e4"
          class="text-none white--text font-weight-bold"
          :disabled="regraJaExiste"
          @click="adicionarRegra"
        >
          <v-icon left small>mdi-plus</v-icon>
          {{ $t("Adicionar") }}
        </v-btn>
      </div>

      <div
        v-for="erro in errosFaixas"
        :key="erro"
        class="regras-aviso regras-aviso--warning"
        role="status"
      >
        <v-icon small>mdi-alert-outline</v-icon>
        <span>
          {{ $t(erro) }}
        </span>
      </div>
      <div
        v-if="destinosNaoCompensados.length"
        class="regras-aviso regras-aviso--info"
        role="status"
      >
        <v-icon small>mdi-information-outline</v-icon>
        <span>
          {{
            $t("Regras_aviso_compensacao", {
              tipos: destinosNaoCompensados.map((tipo) => $t(tipo)).join(", "),
            })
          }}
        </span>
      </div>
    </div>

    <!-- Folha e banco de horas -->
    <div v-if="abaAtual === 'FOLHA'" role="tabpanel">
      <p class="regras-dia__ajuda">
        <v-icon small color="#667085">mdi-file-document-outline</v-icon>
        {{ $t("Regras_folha_ajuda") }}
      </p>
      <div class="regras-tabela">
        <div class="regras-tabela__cabecalho regras-folha__linha">
          <span>{{ $t("Tipo") }}</span>
          <span>{{ $t("Regras_codigo_folha") }}</span>
          <span>{{ $t("Regras_banco_horas") }}</span>
        </div>
        <div
          v-for="linha in linhasFolha"
          :key="linha.tipo"
          class="regras-folha__linha"
        >
          <span>
            <strong>{{ $t(linha.tipo) }}</strong>
            <small v-if="linha.usadoNasRegras" class="regras-tag">
              {{ $t("Regras_usado_nas_faixas") }}
            </small>
          </span>
          <span>
            <template v-if="linha.rubrica">
              <span class="regras-codigo">{{ linha.rubrica }}</span>
            </template>
            <template v-else>
              <v-text-field
                v-model="novasRubricas[linha.tipo]"
                :placeholder="$t('Regras_informe_codigo')"
                dense
                outlined
                hide-details
                class="regras-codigo-input"
                :aria-label="`${$t('Regras_codigo_folha')}: ${$t(linha.tipo)}`"
              ></v-text-field>
              <v-btn
                x-small
                text
                color="#5e72e4"
                class="text-none"
                :disabled="!String(novasRubricas[linha.tipo] || '').trim()"
                @click="associarRubrica(linha.tipo)"
              >
                {{ $t("Regras_associar") }}
              </v-btn>
            </template>
          </span>
          <span>
            <v-switch
              :input-value="linha.noBanco"
              inset
              dense
              hide-details
              color="#5e72e4"
              class="mt-0 pt-0"
              :aria-label="`${$t('Regras_banco_horas')}: ${$t(linha.tipo)}`"
              @change="alternarBancoHoras(linha.tipo, $event === true)"
            ></v-switch>
          </span>
        </div>
      </div>
      <v-switch
        :input-value="recurso.bancoHorasUtilizarMultiplicadorVolatil === true"
        :label="$t('Regras_multiplicador_bh')"
        :hint="$t('Regras_multiplicador_bh_ajuda')"
        persistent-hint
        inset
        dense
        color="#5e72e4"
        class="regras-multiplicador"
        @change="
          $set(
            recurso,
            'bancoHorasUtilizarMultiplicadorVolatil',
            $event === true
          )
        "
      ></v-switch>
      <div
        v-if="tiposSemRubrica.length"
        class="regras-aviso regras-aviso--warning"
        role="status"
      >
        <v-icon small>mdi-alert-outline</v-icon>
        <span>
          {{
            $t("Regras_aviso_sem_rubrica", {
              tipos: tiposSemRubrica.map((tipo) => $t(tipo)).join(", "),
            })
          }}
        </span>
      </div>
    </div>

    <!-- Simulador -->
    <div v-if="abaAtual === 'SIMULADOR'" role="tabpanel">
      <p class="regras-dia__ajuda">
        <v-icon small color="#667085">mdi-flask-outline</v-icon>
        {{ $t("Regras_simulador_ajuda") }}
      </p>
      <div class="regras-simulador__entradas">
        <v-text-field
          v-model="simulacaoEntrada.data"
          type="date"
          :label="$t('Data')"
          dense
          outlined
          hide-details
        ></v-text-field>
        <v-checkbox
          v-model="simulacaoEntrada.feriado"
          :label="$t('Regras_simulador_feriado')"
          dense
          hide-details
          class="mt-0"
        ></v-checkbox>
        <v-select
          v-model="simulacaoEntrada.tipoExtra"
          :items="itensTipos"
          :label="$t('Regras_tipo_apurado')"
          dense
          outlined
          hide-details
        ></v-select>
        <v-text-field
          v-model="simulacaoEntrada.previstas"
          :label="$t('Regras_marcacoes_previstas')"
          placeholder="08:00 12:00 13:00 17:00"
          dense
          outlined
          hide-details
        ></v-text-field>
        <v-text-field
          v-model="simulacaoEntrada.realizadas"
          :label="$t('Regras_marcacoes_realizadas')"
          placeholder="08:07 12:00 13:00 19:40"
          dense
          outlined
          hide-details
        ></v-text-field>
      </div>

      <div class="regras-simulador__resultado" aria-live="polite">
        <div class="regras-passo">
          <span class="regras-passo__numero">1</span>
          <div>
            <strong>{{ $t("Regras_passo_tipo_dia") }}</strong>
            <span>{{ $t(`Situacao_dia_${simulacao.situacao}`) }}</span>
          </div>
        </div>
        <div class="regras-passo">
          <span class="regras-passo__numero">2</span>
          <div>
            <strong>{{ $t("Regras_passo_tolerancia") }}</strong>
            <span>
              {{
                $t("Regras_tolerancia_resumo", {
                  extras: simulacao.tolerancia.extras,
                  debitos: simulacao.tolerancia.debitos,
                })
              }}
            </span>
            <small v-if="simulacao.tolerancia.variacoes">
              {{ $t("Regras_variacoes") }}:
              {{
                simulacao.tolerancia.variacoes
                  .map((v) => `${v} min`)
                  .join(" · ")
              }}
            </small>
            <small
              v-if="simulacao.tolerancia.excedeuMarcacao"
              class="regras-passo__alerta"
            >
              {{ $t("Regras_marcacao_excedida") }}
            </small>
          </div>
        </div>
        <div class="regras-passo">
          <span class="regras-passo__numero">3</span>
          <div>
            <strong>{{ $t("Regras_passo_apuracao") }}</strong>
            <span>
              {{ $t("Regras_extras") }}: {{ formatarHoras(simulacao.extras) }} ·
              {{ $t("ATRASO") }}: {{ formatarHoras(simulacao.atraso) }} ·
              {{ $t("COMPENSAR") }}: {{ formatarHoras(simulacao.compensar) }}
            </span>
          </div>
        </div>
        <div class="regras-passo">
          <span class="regras-passo__numero">4</span>
          <div class="regras-passo__tabela">
            <strong>{{ $t("Regras_passo_faixas") }}</strong>
            <table v-if="simulacao.linhas.length">
              <thead>
                <tr>
                  <th scope="col">{{ $t("Tipo") }}</th>
                  <th scope="col">%</th>
                  <th scope="col">{{ $t("Horas") }}</th>
                  <th scope="col">{{ $t("Regras_codigo_folha") }}</th>
                  <th scope="col">{{ $t("Regras_banco_horas") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="linha in simulacao.linhas" :key="linha.tipo">
                  <th scope="row">{{ $t(linha.tipo) }}</th>
                  <td>{{ linha.percentual }}%</td>
                  <td>{{ formatarHoras(linha.minutos) }}</td>
                  <td>{{ linha.rubrica || "—" }}</td>
                  <td>
                    {{
                      linha.noBanco ? formatarHoras(linha.minutosBanco) : "—"
                    }}
                  </td>
                </tr>
              </tbody>
            </table>
            <span v-else>{{ $t("Regras_sem_extras") }}</span>
            <small v-if="simulacao.linhas.length && !simulacao.faixasAplicadas">
              {{ $t("Regras_sem_faixas_aplicaveis") }}
            </small>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import {
  CREDITOS_PADRAO,
  formatarHoras,
  parseHoras,
} from "@/utils/compensacaoHoras";
import {
  SITUACOES_DIA,
  TIPOS_HORA_EXTRA,
  TOLERANCIA_PADRAO_MINUTOS,
  TOLERANCIA_MAXIMA_MINUTOS,
  simularDia,
  validarNiveis,
} from "@/utils/regrasTipoDia";

// Edita as regras diretamente no sindicato recebido (mesmo objeto salvo pelo formulário).
export default {
  name: "RegrasTipoDiaSindicato",

  props: {
    recurso: { type: Object, required: true },
  },

  data() {
    return {
      uid: `regras-dia-${Math.floor(Math.random() * 1000000)}`,
      abaAtual: "TOLERANCIAS",
      novaRegra: { origem: "EXTRA50", situacao: "DIA_UTIL" },
      novasRubricas: {},
      simulacaoEntrada: {
        data: "2026-09-02",
        feriado: false,
        tipoExtra: "EXTRA50",
        previstas: "08:00 12:00 13:00 17:00",
        realizadas: "08:07 12:00 13:00 19:40",
      },
    };
  },

  computed: {
    niveis() {
      return Array.isArray(this.recurso.niveisHora)
        ? this.recurso.niveisHora
        : [];
    },
    tolerancias() {
      return Array.isArray(this.recurso.tolerancias)
        ? this.recurso.tolerancias
        : [];
    },
    abas() {
      return [
        {
          valor: "TOLERANCIAS",
          titulo: "Regras_aba_tolerancias",
          icone: "mdi-timer-sand",
          contador: this.tolerancias.length,
        },
        {
          valor: "FAIXAS",
          titulo: "Regras_aba_faixas",
          icone: "mdi-stairs",
          contador: this.gruposFaixas.length,
        },
        {
          valor: "FOLHA",
          titulo: "Regras_aba_folha",
          icone: "mdi-file-document-outline",
          contador: this.tiposSemRubrica.length || null,
        },
        {
          valor: "SIMULADOR",
          titulo: "Regras_aba_simulador",
          icone: "mdi-flask-outline",
        },
      ];
    },
    itensTipos() {
      return TIPOS_HORA_EXTRA.map((tipo) => ({
        text: this.$t(tipo),
        value: tipo,
      }));
    },
    itensSituacoes() {
      return SITUACOES_DIA.map((situacao) => ({
        text: this.$t(`Situacao_dia_${situacao}`),
        value: situacao,
      }));
    },
    linhasTolerancia() {
      return SITUACOES_DIA.map((situacao) => {
        const configuracao = this.tolerancias.find(
          (item) => item?.situacao === situacao
        );
        const todos = this.tolerancias.find(
          (item) => item?.situacao === "TODOS"
        );
        const efetiva = configuracao || todos;
        return {
          situacao,
          configuracao,
          heranca:
            situacao !== "TODOS" && todos
              ? this.$t("Situacao_dia_TODOS")
              : this.$t("Regras_padrao_legal"),
          efetiva: {
            extras: efetiva
              ? efetiva.toleranciaExtrasMinutos ?? TOLERANCIA_PADRAO_MINUTOS
              : TOLERANCIA_PADRAO_MINUTOS,
            debitos: efetiva
              ? efetiva.toleranciaDebitosMinutos ?? TOLERANCIA_PADRAO_MINUTOS
              : TOLERANCIA_PADRAO_MINUTOS,
            porMarcacao: efetiva?.toleranciaPorMarcacao === true,
          },
        };
      });
    },
    gruposFaixas() {
      const grupos = [];
      this.niveis.forEach((nivel) => {
        const chave = `${nivel.origem}:${nivel.situacao}`;
        let grupo = grupos.find((item) => item.chave === chave);
        if (!grupo) {
          grupo = {
            chave,
            origem: nivel.origem,
            situacao: nivel.situacao,
            niveis: [],
          };
          grupos.push(grupo);
        }
        grupo.niveis.push(nivel);
      });
      grupos.forEach((grupo) => {
        grupo.niveis.sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0));
        grupo.possuiRestante = grupo.niveis.some(
          (nivel) =>
            nivel.limiteHoras === null || nivel.limiteHoras === undefined
        );
      });
      return grupos;
    },
    regraJaExiste() {
      return this.niveis.some(
        (nivel) =>
          nivel.origem === this.novaRegra.origem &&
          nivel.situacao === this.novaRegra.situacao
      );
    },
    errosFaixas() {
      return validarNiveis(this.niveis);
    },
    destinosUsados() {
      return [...new Set(this.niveis.map((nivel) => nivel.destino))];
    },
    destinosNaoCompensados() {
      const creditos =
        this.recurso.compensacaoPersonalizada === true
          ? this.recurso.compensacaoCreditos || []
          : CREDITOS_PADRAO;
      return this.destinosUsados.filter((tipo) => !creditos.includes(tipo));
    },
    linhasFolha() {
      const rubricas = Array.isArray(this.recurso.rubricas)
        ? this.recurso.rubricas
        : [];
      const bancoHoras = Array.isArray(this.recurso.apuracoesBh)
        ? this.recurso.apuracoesBh
        : [];
      return TIPOS_HORA_EXTRA.map((tipo) => ({
        tipo,
        rubrica: rubricas.find((item) => item?.tipo === tipo)?.rubrica || null,
        noBanco: bancoHoras.some((item) => item?.tipo === tipo),
        usadoNasRegras:
          this.destinosUsados.includes(tipo) ||
          this.niveis.some((nivel) => nivel.origem === tipo),
      }));
    },
    tiposSemRubrica() {
      return this.linhasFolha
        .filter((linha) => linha.usadoNasRegras && !linha.rubrica)
        .map((linha) => linha.tipo);
    },
    simulacao() {
      const marcacoes = (texto) =>
        String(texto || "")
          .split(/[\s,;]+/)
          .map((item) => item.trim())
          .filter(Boolean);
      return simularDia({
        data: this.simulacaoEntrada.data,
        feriado: this.simulacaoEntrada.feriado === true,
        previstas: marcacoes(this.simulacaoEntrada.previstas),
        realizadas: marcacoes(this.simulacaoEntrada.realizadas),
        tipoExtra: this.simulacaoEntrada.tipoExtra,
        niveis: this.niveis,
        tolerancias: this.tolerancias,
        rubricas: this.recurso.rubricas,
        apuracoesBh: this.recurso.apuracoesBh,
        multiplicadorBh:
          this.recurso.bancoHorasUtilizarMultiplicadorVolatil === true,
      });
    },
  },

  methods: {
    formatarHoras,

    lista(campo) {
      if (!Array.isArray(this.recurso[campo])) {
        this.$set(this.recurso, campo, []);
      }
      return this.recurso[campo];
    },

    alternarTolerancia(situacao, ativa) {
      const tolerancias = this.lista("tolerancias");
      const indice = tolerancias.findIndex(
        (item) => item?.situacao === situacao
      );
      if (ativa && indice < 0) {
        tolerancias.push({
          situacao,
          toleranciaExtrasMinutos: TOLERANCIA_PADRAO_MINUTOS,
          toleranciaDebitosMinutos: TOLERANCIA_PADRAO_MINUTOS,
          toleranciaPorMarcacao: false,
        });
      } else if (!ativa && indice >= 0) {
        tolerancias.splice(indice, 1);
      }
    },

    // Limita aos minutos permitidos pela CLT (art. 58, § 1º)
    definirMinutos(configuracao, campo, valor) {
      const minutos = Number(valor);
      this.$set(
        configuracao,
        campo,
        Number.isFinite(minutos)
          ? Math.max(
              0,
              Math.min(TOLERANCIA_MAXIMA_MINUTOS, Math.round(minutos))
            )
          : TOLERANCIA_PADRAO_MINUTOS
      );
    },

    adicionarRegra() {
      if (this.regraJaExiste) {
        return;
      }
      this.lista("niveisHora").push({
        origem: this.novaRegra.origem,
        situacao: this.novaRegra.situacao,
        ordem: 1,
        limiteHoras: 2,
        destino: this.novaRegra.origem,
      });
      this.abaAtual = "FAIXAS";
    },

    adicionarFaixa(grupo, restante) {
      const ultima = grupo.niveis[grupo.niveis.length - 1];
      const ultimoLimite = ultima?.limiteHoras ?? 0;
      this.lista("niveisHora").push({
        origem: grupo.origem,
        situacao: grupo.situacao,
        ordem: (ultima?.ordem ?? 0) + 1,
        limiteHoras: restante ? null : ultimoLimite + 2,
        destino: ultima?.destino || grupo.origem,
      });
    },

    definirLimite(nivel, valor) {
      const minutos = parseHoras(valor);
      if (minutos !== null && minutos > 0) {
        this.$set(nivel, "limiteHoras", minutos / 60);
      }
    },

    removerNivel(nivel) {
      const niveis = this.lista("niveisHora");
      niveis.splice(niveis.indexOf(nivel), 1);
      this.reordenar(nivel.origem, nivel.situacao);
    },

    removerGrupo(grupo) {
      this.$set(
        this.recurso,
        "niveisHora",
        this.niveis.filter(
          (nivel) =>
            !(
              nivel.origem === grupo.origem && nivel.situacao === grupo.situacao
            )
        )
      );
    },

    reordenar(origem, situacao) {
      this.niveis
        .filter(
          (nivel) => nivel.origem === origem && nivel.situacao === situacao
        )
        .sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0))
        .forEach((nivel, indice) => this.$set(nivel, "ordem", indice + 1));
    },

    // Altera os mesmos arrays exibidos nas tabelas de rubricas e de apurações do banco de horas.
    associarRubrica(tipo) {
      const codigo = String(this.novasRubricas[tipo] || "").trim();
      if (!codigo) {
        return;
      }
      this.lista("rubricas").push({
        tipo,
        rubrica: codigo,
        tipoApuracao: "HORA",
      });
      this.$set(this.novasRubricas, tipo, "");
    },

    alternarBancoHoras(tipo, ativo) {
      const apuracoes = this.lista("apuracoesBh");
      const indice = apuracoes.findIndex((item) => item?.tipo === tipo);
      if (ativo && indice < 0) {
        apuracoes.push({ tipo });
      } else if (!ativo && indice >= 0) {
        apuracoes.splice(indice, 1);
      }
    },
  },
};
</script>

<style scoped>
/* O tema não exibe o contorno dos campos "outlined"; reforça que são editáveis */
.regras-dia ::v-deep .v-text-field--outlined .v-input__slot {
  background: #ffffff;
  border-radius: 8px;
  box-shadow: inset 0 0 0 1px #d0d5dd;
}

.regras-aviso {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  margin-top: 12px;
  padding: 10px 12px;
  font-size: 0.84rem;
  line-height: 1.4;
  border: 1px solid;
  border-radius: 10px;
}

.regras-aviso .v-icon {
  color: inherit !important;
}

.regras-aviso--info {
  color: #1d3fa6;
  background: #eef4ff;
  border-color: #c7d7fe;
}

.regras-aviso--warning {
  color: #93370d;
  background: #fffaeb;
  border-color: #fedf89;
}
.regras-dia {
  --regras-ink: #344054;
  --regras-muted: #667085;
  --regras-border: #eaecf0;
  --regras-primary: #5e72e4;
  padding: 20px;
  color: var(--regras-ink);
  background: #ffffff;
  border: 1px solid var(--regras-border);
  border-radius: 14px;
}

.regras-dia__header {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  margin-bottom: 16px;
}

.regras-dia__icone {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(94, 114, 228, 0.1);
  border-radius: 10px;
}

.regras-dia__eyebrow {
  color: var(--regras-primary);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.regras-dia__titulo {
  margin: 2px 0 4px;
  font-size: 1.05rem;
  font-weight: 700;
}

.regras-dia__descricao,
.regras-dia__nota {
  margin: 0;
  color: var(--regras-muted);
  font-size: 0.86rem;
}

.regras-dia__nota {
  margin-top: 10px;
  font-size: 0.78rem;
}

.regras-dia__ajuda {
  display: flex;
  gap: 6px;
  align-items: flex-start;
  margin: 0 0 12px;
  color: var(--regras-muted);
  font-size: 0.82rem;
}

.regras-abas {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 16px;
  padding: 4px;
  background: #f2f4f7;
  border-radius: 10px;
}

.regras-aba {
  display: inline-flex;
  gap: 6px;
  align-items: center;
  min-height: 34px;
  padding: 0 14px;
  color: #475467;
  font-size: 0.82rem;
  font-weight: 700;
  border-radius: 8px;
}

.regras-aba--ativa {
  color: var(--regras-ink);
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.12);
}

.regras-aba:focus-visible {
  outline: 2px solid var(--regras-primary);
  outline-offset: 2px;
}

.regras-aba__contador {
  min-width: 18px;
  padding: 0 6px;
  color: var(--regras-primary);
  font-size: 0.7rem;
  background: rgba(94, 114, 228, 0.12);
  border-radius: 999px;
}

.regras-tabela {
  overflow-x: auto;
  border: 1px solid var(--regras-border);
  border-radius: 12px;
}

.regras-tabela__cabecalho {
  color: var(--regras-muted);
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  background: #f9fafb;
}

.regras-tolerancia__linha,
.regras-folha__linha {
  display: grid;
  gap: 12px;
  align-items: center;
  min-width: 560px;
  padding: 10px 14px;
  border-bottom: 1px solid var(--regras-border);
}

.regras-tolerancia__linha {
  grid-template-columns: minmax(200px, 1.6fr) 1fr 1fr 0.8fr;
}

.regras-folha__linha {
  grid-template-columns: minmax(180px, 1.4fr) 1.4fr 0.6fr;
}

.regras-tolerancia__linha:last-child,
.regras-folha__linha:last-child {
  border-bottom: 0;
}

.regras-tolerancia__linha--herdada {
  color: var(--regras-muted);
}

.regras-tolerancia__situacao {
  display: flex;
  gap: 8px;
  align-items: center;
}

.regras-tolerancia__situacao span,
.regras-folha__linha > span:first-child {
  display: flex;
  flex-direction: column;
}

.regras-tolerancia__situacao small {
  font-size: 0.74rem;
}

.regras-tolerancia__herdado {
  font-size: 0.86rem;
}

.regras-minutos {
  max-width: 110px;
}

.regras-vazio {
  padding: 14px;
  color: var(--regras-muted);
  font-size: 0.84rem;
  text-align: center;
  border: 1px dashed #d0d5dd;
  border-radius: 12px;
}

.regras-grupo {
  margin-bottom: 10px;
  padding: 12px 14px;
  background: #f9fafb;
  border: 1px solid var(--regras-border);
  border-left: 3px solid var(--regras-primary);
  border-radius: 12px;
}

.regras-grupo__cabecalho {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 0.88rem;
}

.regras-faixas {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.regras-faixas li {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  padding: 6px 10px;
  background: #ffffff;
  border: 1px solid var(--regras-border);
  border-radius: 10px;
}

.regras-faixas__ordem {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  color: var(--regras-primary);
  font-size: 0.74rem;
  font-weight: 700;
  background: rgba(94, 114, 228, 0.1);
  border-radius: 50%;
}

.regras-faixas__limite {
  display: inline-flex;
  gap: 6px;
  align-items: center;
  font-size: 0.84rem;
}

.regras-destino {
  max-width: 190px;
}

.regras-grupo__acoes {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
}

.regras-nova {
  display: grid;
  grid-template-columns: auto minmax(160px, 1fr) minmax(160px, 1fr) auto;
  gap: 10px;
  align-items: center;
  margin-top: 12px;
  padding: 12px 14px;
  border: 1px dashed #d0d5dd;
  border-radius: 12px;
}

.regras-nova > span {
  color: var(--regras-muted);
  font-size: 0.82rem;
  font-weight: 600;
}

.regras-tag {
  width: fit-content;
  margin-top: 2px;
  padding: 1px 8px;
  color: var(--regras-primary);
  font-size: 0.7rem;
  font-weight: 600;
  background: rgba(94, 114, 228, 0.1);
  border-radius: 999px;
}

.regras-codigo {
  padding: 2px 10px;
  font-family: monospace;
  font-weight: 700;
  background: #f2f4f7;
  border-radius: 6px;
}

.regras-codigo-input {
  display: inline-block;
  max-width: 140px;
}

.regras-multiplicador {
  margin-top: 14px;
}

.regras-simulador__entradas {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  align-items: center;
}

.regras-simulador__resultado {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 14px;
}

.regras-passo {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 10px 14px;
  background: #f9fafb;
  border: 1px solid var(--regras-border);
  border-radius: 12px;
}

.regras-passo > div {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  font-size: 0.86rem;
}

.regras-passo small {
  color: var(--regras-muted);
  font-size: 0.76rem;
}

.regras-passo__alerta {
  color: #b42318 !important;
  font-weight: 600;
}

.regras-passo__numero {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: #ffffff;
  font-size: 0.76rem;
  font-weight: 700;
  background: var(--regras-primary);
  border-radius: 50%;
}

.regras-passo__tabela {
  flex: 1 1 auto;
  overflow-x: auto;
}

.regras-passo table {
  width: 100%;
  min-width: 420px;
  margin-top: 6px;
  border-collapse: collapse;
  font-size: 0.84rem;
}

.regras-passo th,
.regras-passo td {
  padding: 5px 8px;
  text-align: left;
  border-bottom: 1px solid var(--regras-border);
}

.regras-passo thead th {
  color: var(--regras-muted);
  font-size: 0.72rem;
  text-transform: uppercase;
}

@media (max-width: 960px) {
  .regras-nova,
  .regras-simulador__entradas {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (max-width: 600px) {
  .regras-dia {
    padding: 16px;
  }
}
</style>
